import type { DataSelector } from "@json2csv/plainjs";
import { Parser } from "@json2csv/plainjs";
import { maxBy } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODateTimeShort } from "@entrilia/shared/utilities/dateUtils";
import { getUserTimeZone } from "@entrilia/shared/utilities/timeZone";
import type {
  DocumentInfo,
  EmailTrackingItem,
  MessageDeliveryInfo,
  MessageLevel,
} from "../../../../api/types/documentCollectionTypes";

interface DocumentInfoModel {
  name: string;
  investorTitle?: string;
  publishingStatus: string;
  portalPublishingStatus: string;
  category: string;
  isViewed: boolean;
  viewedAt?: string;
  deliveries: MessageDeliveryInfo[];
}

const withUserTimeZone = (label: string): string => `${label} (${getUserTimeZone()})`;

const createEmailsCsvParser = (): Parser<EmailTrackingItem> =>
  new Parser<EmailTrackingItem>({
    fields: [
      {
        label: "Recipient Name",
        value: "recipientName",
      },
      {
        label: "Recipient Email",
        value: "email",
      },
      {
        label: "Investor",
        value: "investor",
      },
      {
        label: "Subject",
        value: "subject",
      },
      {
        label: withUserTimeZone("Sent Date"),
        value: (row) => convertISODateTimeShort(row.sentAt),
      },
      {
        label: "Status",
        value: "status",
      },
    ],
  });

export const emailsToCsv = (emails: EmailTrackingItem[]): string => {
  const csvParser = createEmailsCsvParser();
  return csvParser.parse(emails);
};

const getRecipientFields = (length: number) => {
  const fields: DataSelector<DocumentInfoModel>[] = [];

  for (let i = 0; i < length; ++i) {
    fields.push({ label: `(${i + 1}) Recipient Name`, value: (row) => row.deliveries[i]?.recipient.name });
    fields.push({ label: `(${i + 1}) Recipient Email`, value: (row) => row.deliveries[i]?.recipient.email });
    fields.push({ label: `(${i + 1}) Recipient Viewed`, value: (row) => row.deliveries[i]?.isRead });
    fields.push({
      label: withUserTimeZone(`(${i + 1}) Recipient Viewed At`),
      value: (row) => convertISODateTimeShort(row.deliveries[i]?.readDate ?? ""),
    });
  }

  return fields;
};

const investorPortalFields: DataSelector<DocumentInfoModel>[] = [
  {
    label: "Investor Portal",
    value: "portalPublishingStatus",
  },
];

const getViewTrackingFields = (): DataSelector<DocumentInfoModel>[] => [
  {
    label: "Viewed",
    value: "isViewed",
  },
  {
    label: withUserTimeZone("Viewed At"),
    value: (row) => (row.viewedAt ? convertISODateTimeShort(row.viewedAt) : ""),
  },
];

const createDocumentsCsvParser = (maxRecipients: number, investorPortalEnabled: boolean): Parser<DocumentInfoModel> =>
  new Parser<DocumentInfoModel>({
    fields: [
      {
        label: "Name",
        value: "name",
      },
      {
        label: "Investor",
        value: "investorTitle",
      },
      ...(investorPortalEnabled ? investorPortalFields : []),
      {
        label: "Status",
        value: "publishingStatus",
      },
      {
        label: "Category",
        value: "category",
      },
      ...(investorPortalEnabled ? getViewTrackingFields() : []),
      ...getRecipientFields(maxRecipients),
    ],
  });

const getInvestorLevelRows = (documents: DocumentInfo[]): DocumentInfoModel[] =>
  documents.map((doc) => ({
    ...doc,
    portalPublishingStatus: doc.publishToInvestorPortal !== false ? "Published" : "Not Published",
  }));

const getFundLevelRows = (documents: DocumentInfo[]): DocumentInfoModel[] =>
  documents.flatMap((doc) =>
    doc.investorDeliveries.map((delivery) => ({
      name: doc.name,
      investorTitle: delivery.investorTitle,
      category: doc.category,
      publishingStatus: doc.publishingStatus,
      portalPublishingStatus: doc.publishToInvestorPortal !== false ? "Published" : "Not Published",
      isViewed: delivery.isViewed,
      viewedAt: delivery.viewedAt,
      deliveries: delivery.deliveries,
    }))
  );

export const documentsToCsv = (
  documents: DocumentInfo[],
  messageLevel: MessageLevel,
  investorPortalEnabled: boolean
): string => {
  const rows = messageLevel === "Fund" ? getFundLevelRows(documents) : getInvestorLevelRows(documents);

  const maxRecipients = maxBy(
    rows.map((doc) => doc.deliveries.length),
    (count) => count
  );

  const csvParser = createDocumentsCsvParser(maxRecipients, investorPortalEnabled);
  return csvParser.parse(rows);
};
